// Sign Module API Service
// Handles all API calls related to document signing

import { get, post } from '@/api/lib/amplify-wrapper'
import { createApiHeaders, getTenantDomain } from '@/lib/services/api-utils'
import type {
  SignDocument,
  SessionCreationRequest,
  SessionCreationResponse,
  SessionError,
  TenantSignConfig,
  SignatureData,
  SessionCompletionData
} from '@/types/sign'

const API_NAME = 'sign'

/**
 * List sign documents for the current user
 * Returns documents where the user is a signer
 */
export const listSignDocuments = async (params?: {
  status?: string
  limit?: number
  nextToken?: string
}): Promise<{
  items: SignDocument[]
  pagination?: {
    nextToken?: string
    count?: number
  }
}> => {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.append('status', params.status)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const restOperation = get({
    apiName: API_NAME,
    path: `/sign/documents?${queryParams.toString()}`,
    options: {
      headers: createApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = (await response.body.json()) as unknown as SignDocument[]

  return {
    items: data,
    pagination: {
      nextToken: response.headers['x-next-token'],
      count: response.headers['x-item-count']
        ? parseInt(response.headers['x-item-count'])
        : data.length
    }
  }
}

/**
 * Get a specific sign document by ID
 */
export const getSignDocument = async (documentId: string): Promise<SignDocument> => {
  const restOperation = get({
    apiName: API_NAME,
    path: `/sign/documents/${documentId}`,
    options: {
      headers: createApiHeaders()
    }
  })

  const response = await restOperation.response
  return (await response.body.json()) as unknown as SignDocument
}

/**
 * Create a signing session
 * This is the key endpoint that determines whether instant signing or OTP is used
 */
export const createSigningSession = async (
  request: SessionCreationRequest
): Promise<SessionCreationResponse> => {
  const tenantDomain = getTenantDomain()

  const restOperation = post({
    apiName: API_NAME,
    path: '/sign/sessions',
    options: {
      body: {
        ...request,
        tenantDomain
      },
      headers: createApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = (await response.body.json()) as unknown as SessionCreationResponse

  return data
}

/**
 * Save signature for a session
 */
export const saveSignature = async (
  sessionId: string,
  signatureData: SignatureData
): Promise<void> => {
  const restOperation = post({
    apiName: API_NAME,
    path: `/sign/sessions/${sessionId}/signature`,
    options: {
      body: signatureData as any,
      headers: createApiHeaders()
    }
  })

  await restOperation.response
}

/**
 * Complete a signing session
 */
export const completeSession = async (
  sessionId: string,
  completionData?: Partial<SessionCompletionData>
): Promise<void> => {
  const restOperation = post({
    apiName: API_NAME,
    path: `/sign/sessions/${sessionId}/complete`,
    options: {
      body: (completionData || {}) as any,
      headers: createApiHeaders()
    }
  })

  await restOperation.response
}

/**
 * Get tenant sign configuration
 * Returns tenant-specific signing settings including instant signing enabled status
 */
export const getTenantSignConfig = async (
  tenantId: string
): Promise<TenantSignConfig> => {
  const restOperation = get({
    apiName: API_NAME,
    path: `/tenants/${tenantId}/config/sign`,
    options: {
      headers: createApiHeaders()
    }
  })

  const response = await restOperation.response
  return (await response.body.json()) as unknown as TenantSignConfig
}

/**
 * Send OTP for verification
 * Used when instant signing is not available
 */
export const sendOtp = async (sessionId: string, method: 'email' | 'sms'): Promise<void> => {
  const restOperation = post({
    apiName: API_NAME,
    path: `/sign/sessions/${sessionId}/send-otp`,
    options: {
      body: { method },
      headers: createApiHeaders()
    }
  })

  await restOperation.response
}

/**
 * Verify OTP
 */
export const verifyOtp = async (sessionId: string, otp: string): Promise<void> => {
  const restOperation = post({
    apiName: API_NAME,
    path: `/sign/sessions/${sessionId}/verify-otp`,
    options: {
      body: { otp },
      headers: createApiHeaders()
    }
  })

  await restOperation.response
}

/**
 * Error handling helper
 * Converts Amplify API errors to SessionError
 */
export const handleSignApiError = (error: unknown): SessionError => {
  if (error && typeof error === 'object' && 'response' in error) {
    const apiError = error as {
      response?: {
        statusCode?: number
        body?: { message?: string; code?: string }
      }
    }

    return {
      message:
        apiError.response?.body?.message || 'An error occurred while processing your request',
      code: apiError.response?.body?.code,
      statusCode: apiError.response?.statusCode || 500
    }
  }

  return {
    message: error instanceof Error ? error.message : 'Unknown error occurred',
    statusCode: 500
  }
}
